import React, { useState, useEffect } from "react";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { 
  Settings, 
  Save, 
  RotateCcw, 
  Eye, 
  EyeOff, 
  Mic, 
  MicOff, 
  Video, 
  VideoOff,
  MessageSquare,
  MessageSquareOff,
  Users,
  Shield,
  Clock,
  AlertTriangle,
  CheckCircle,
  Info
} from "lucide-react";
import { Button } from "src/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "src/components/ui/Card";
import { Label } from "src/components/ui/Label";
import { Switch } from "src/components/ui/Switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/Select";
import { Input } from "src/components/ui/Input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "src/components/ui/Tabs";
import { Badge } from "src/components/ui/Badge";
import { Separator } from "src/components/ui/Separator";
import { Alert, AlertDescription } from "src/components/ui/Alert";
import { toast } from "src/hooks/use-toast";

// Types for meeting configuration
interface MeetingConfiguration {
  // Anonymous access settings
  allowAnonymousUsersToJoinMeeting: boolean;
  allowAnonymousUsersToStartMeeting: boolean;
  
  // Admission settings
  autoAdmittedUsers: 'everyone' | 'everyoneInCompany' | 'everyoneInSameAndFederatedCompany' | 'everyoneInCompanyExcludingGuests' | 'organizer';
  
  // Lobby settings
  lobbyBypassSettings: {
    scope: 'everyone' | 'everyoneInCompany' | 'everyoneInSameAndFederatedCompany' | 'everyoneInCompanyExcludingGuests' | 'organizer';
    isDialInBypassEnabled: boolean;
  };
  
  // Presenter permissions
  allowedPresenters: 'everyone' | 'organization' | 'roleIsPresenter' | 'organizer';
  
  // Participant permissions
  allowAttendeeToEnableCamera: boolean;
  allowAttendeeToEnableMic: boolean;
  allowParticipantsToChangeName: boolean;
  
  // Communication settings
  allowMeetingChat: 'enabled' | 'disabled' | 'limited';
  
  // Recording and transcription
  recordAutomatically: boolean;
  allowRecording: boolean;
  allowTranscription: boolean;
  
  // Additional features
  allowTeamworkReactions: boolean;
  isEntryExitAnnounced: boolean;
  
  // Meeting duration
  meetingDuration: number; // in minutes
  
  // Security settings
  watermarkProtection: {
    enabledForContentSharing: boolean;
    enabledForVideo: boolean;
  };
}

interface SessionTypeConfig {
  sessionType: string;
  displayName: string;
  description: string;
  icon: React.ReactNode;
  config: MeetingConfiguration;
}

const SessionConfiguration = () => {
  const { setTitle, setDescription, setActions, reset } = usePageHeaderStore();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("demo");
  const [hasChanges, setHasChanges] = useState(false);

  // Default configuration
  const defaultConfig: MeetingConfiguration = {
    allowAnonymousUsersToJoinMeeting: true,
    allowAnonymousUsersToStartMeeting: false,
    autoAdmittedUsers: 'everyone',
    lobbyBypassSettings: {
      scope: 'everyone',
      isDialInBypassEnabled: true
    },
    allowedPresenters: 'organizer',
    allowAttendeeToEnableCamera: true,
    allowAttendeeToEnableMic: true,
    allowParticipantsToChangeName: true,
    allowMeetingChat: 'enabled',
    recordAutomatically: false,
    allowRecording: true,
    allowTranscription: true,
    allowTeamworkReactions: true,
    isEntryExitAnnounced: false,
    meetingDuration: 60,
    watermarkProtection: {
      enabledForContentSharing: true,
      enabledForVideo: false
    }
  };

  // Session type configurations
  const [sessionConfigs, setSessionConfigs] = useState<SessionTypeConfig[]>([
    {
      sessionType: 'demo',
      displayName: 'Demo Sessions',
      description: 'Trial sessions for new students with restricted features',
      icon: <Eye className="w-5 h-5" />,
      config: {
        ...defaultConfig,
        allowAttendeeToEnableCamera: false,
        allowAttendeeToEnableMic: false,
        allowRecording: false,
        allowTranscription: false,
        meetingDuration: 30,
        lobbyBypassSettings: {
          scope: 'organizer',
          isDialInBypassEnabled: false
        }
      }
    },
    {
      sessionType: 'regular',
      displayName: 'Regular Sessions',
      description: 'Standard tutoring sessions with full features',
      icon: <Users className="w-5 h-5" />,
      config: {
        ...defaultConfig,
        allowedPresenters: 'everyone',
        allowMeetingChat: 'enabled'
      }
    },
    {
      sessionType: 'assessment',
      displayName: 'Assessment Sessions',
      description: 'Evaluation sessions with enhanced monitoring',
      icon: <Shield className="w-5 h-5" />,
      config: {
        ...defaultConfig,
        recordAutomatically: true,
        allowTranscription: true,
        allowParticipantsToChangeName: false,
        allowMeetingChat: 'disabled',
        isEntryExitAnnounced: true,
        allowedPresenters: 'organizer'
      }
    },
    {
      sessionType: 'makeup',
      displayName: 'Makeup Sessions',
      description: 'Rescheduled sessions with extended duration',
      icon: <Clock className="w-5 h-5" />,
      config: {
        ...defaultConfig,
        meetingDuration: 90,
        allowedPresenters: 'everyone'
      }
    },
    {
      sessionType: 'consultation',
      displayName: 'Consultation Sessions',
      description: 'Advisory sessions with privacy focus',
      icon: <MessageSquare className="w-5 h-5" />,
      config: {
        ...defaultConfig,
        allowRecording: false,
        allowTranscription: false,
        allowedPresenters: 'everyone',
        allowMeetingChat: 'enabled'
      }
    }
  ]);

  useEffect(() => {
    setTitle("Session Configuration");
    setDescription("Configure meeting settings for different session types");
    
    // Set actions in the header
    setActions(
      <div className="flex items-center gap-2">
        {hasChanges && (
          <Badge variant="secondary" className="flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Unsaved Changes
          </Badge>
        )}
        <Button
          onClick={saveConfigurations}
          disabled={!hasChanges || saving}
          className="flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving..." : "Save All Changes"}
        </Button>
      </div>
    );
    
    loadConfigurations();
    return () => reset();
  }, [setTitle, setDescription, setActions, reset, hasChanges, saving]);

  const loadConfigurations = async () => {
    setLoading(true);
    try {
      // TODO: Load configurations from API
      // For now, using default configurations
      console.log("Loading session configurations...");
    } catch (error) {
      console.error("Failed to load configurations:", error);
      toast({
        title: "Error",
        description: "Failed to load session configurations",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateSessionConfig = (sessionType: string, field: string, value: any) => {
    setSessionConfigs(prev => prev.map(config => {
      if (config.sessionType === sessionType) {
        const updatedConfig = { ...config };
        
        // Handle nested fields
        if (field.includes('.')) {
          const [parent, child] = field.split('.');
          updatedConfig.config = {
            ...updatedConfig.config,
            [parent]: {
              ...(updatedConfig.config as any)[parent],
              [child]: value
            }
          };
        } else {
          updatedConfig.config = {
            ...updatedConfig.config,
            [field]: value
          };
        }
        
        return updatedConfig;
      }
      return config;
    }));
    setHasChanges(true);
  };

  const resetToDefaults = (sessionType: string) => {
    const defaultConfigs: Record<string, Partial<MeetingConfiguration>> = {
      demo: {
        allowAttendeeToEnableCamera: false,
        allowAttendeeToEnableMic: false,
        allowRecording: false,
        allowTranscription: false,
        meetingDuration: 30,
        lobbyBypassSettings: {
          scope: 'organizer',
          isDialInBypassEnabled: false
        }
      },
      regular: {
        allowedPresenters: 'everyone',
        allowMeetingChat: 'enabled'
      },
      assessment: {
        recordAutomatically: true,
        allowTranscription: true,
        allowParticipantsToChangeName: false,
        allowMeetingChat: 'disabled',
        isEntryExitAnnounced: true,
        allowedPresenters: 'organizer'
      },
      makeup: {
        meetingDuration: 90,
        allowedPresenters: 'everyone'
      },
      consultation: {
        allowRecording: false,
        allowTranscription: false,
        allowedPresenters: 'everyone',
        allowMeetingChat: 'enabled'
      }
    };

    setSessionConfigs(prev => prev.map(config => {
      if (config.sessionType === sessionType) {
        return {
          ...config,
          config: {
            ...defaultConfig,
            ...defaultConfigs[sessionType]
          }
        };
      }
      return config;
    }));
    setHasChanges(true);
    
    toast({
      title: "Reset Complete",
      description: `${sessionType} session configuration reset to defaults`,
    });
  };

  const saveConfigurations = async () => {
    setSaving(true);
    try {
      // TODO: Save configurations to API
      console.log("Saving configurations:", sessionConfigs);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setHasChanges(false);
      toast({
        title: "Success",
        description: "Session configurations saved successfully",
      });
    } catch (error) {
      console.error("Failed to save configurations:", error);
      toast({
        title: "Error",
        description: "Failed to save configurations",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const getCurrentConfig = (sessionType: string): MeetingConfiguration => {
    const config = sessionConfigs.find(c => c.sessionType === sessionType);
    return config?.config || defaultConfig;
  };

  const renderConfigurationPanel = (sessionType: string) => {
    const config = getCurrentConfig(sessionType);
    const sessionInfo = sessionConfigs.find(c => c.sessionType === sessionType);

    return (
      <div className="space-y-6">
        {/* Session Type Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {sessionInfo?.icon}
              {sessionInfo?.displayName}
            </CardTitle>
            <CardDescription>
              {sessionInfo?.description}
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Access & Security Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Access & Security
            </CardTitle>
            <CardDescription>
              Control who can join and how they access the meeting
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Allow Anonymous Users to Join</Label>
                <p className="text-sm text-muted-foreground">
                  Students can join without Microsoft accounts
                </p>
              </div>
              <Switch
                checked={config.allowAnonymousUsersToJoinMeeting}
                onCheckedChange={(checked) => 
                  updateSessionConfig(sessionType, 'allowAnonymousUsersToJoinMeeting', checked)
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Allow Anonymous Users to Start Meeting</Label>
                <p className="text-sm text-muted-foreground">
                  Anonymous users can start the meeting
                </p>
              </div>
              <Switch
                checked={config.allowAnonymousUsersToStartMeeting}
                onCheckedChange={(checked) => 
                  updateSessionConfig(sessionType, 'allowAnonymousUsersToStartMeeting', checked)
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Auto-Admitted Users</Label>
              <Select
                value={config.autoAdmittedUsers}
                onValueChange={(value) => 
                  updateSessionConfig(sessionType, 'autoAdmittedUsers', value)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="everyone">Everyone</SelectItem>
                  <SelectItem value="everyoneInCompany">Everyone in Company</SelectItem>
                  <SelectItem value="organizer">Organizer Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Lobby Bypass Scope</Label>
              <Select
                value={config.lobbyBypassSettings.scope}
                onValueChange={(value) => 
                  updateSessionConfig(sessionType, 'lobbyBypassSettings.scope', value)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="everyone">Everyone</SelectItem>
                  <SelectItem value="everyoneInCompany">Everyone in Company</SelectItem>
                  <SelectItem value="organizer">Organizer Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Dial-in Bypass Enabled</Label>
                <p className="text-sm text-muted-foreground">
                  Allow phone participants to bypass lobby
                </p>
              </div>
              <Switch
                checked={config.lobbyBypassSettings.isDialInBypassEnabled}
                onCheckedChange={(checked) => 
                  updateSessionConfig(sessionType, 'lobbyBypassSettings.isDialInBypassEnabled', checked)
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Participant Permissions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Participant Permissions
            </CardTitle>
            <CardDescription>
              Configure what participants can do during the meeting
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Who Can Present</Label>
              <Select
                value={config.allowedPresenters}
                onValueChange={(value) => 
                  updateSessionConfig(sessionType, 'allowedPresenters', value)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="organizer">Organizer Only</SelectItem>
                  <SelectItem value="everyone">Everyone</SelectItem>
                  <SelectItem value="organization">Organization Members</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5 flex items-center gap-2">
                <Video className="w-4 h-4" />
                <div>
                  <Label>Allow Camera</Label>
                  <p className="text-sm text-muted-foreground">
                    Students can enable their camera
                  </p>
                </div>
              </div>
              <Switch
                checked={config.allowAttendeeToEnableCamera}
                onCheckedChange={(checked) => 
                  updateSessionConfig(sessionType, 'allowAttendeeToEnableCamera', checked)
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5 flex items-center gap-2">
                <Mic className="w-4 h-4" />
                <div>
                  <Label>Allow Microphone</Label>
                  <p className="text-sm text-muted-foreground">
                    Students can enable their microphone
                  </p>
                </div>
              </div>
              <Switch
                checked={config.allowAttendeeToEnableMic}
                onCheckedChange={(checked) => 
                  updateSessionConfig(sessionType, 'allowAttendeeToEnableMic', checked)
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Allow Name Changes</Label>
                <p className="text-sm text-muted-foreground">
                  Participants can change their display name
                </p>
              </div>
              <Switch
                checked={config.allowParticipantsToChangeName}
                onCheckedChange={(checked) => 
                  updateSessionConfig(sessionType, 'allowParticipantsToChangeName', checked)
                }
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Meeting Chat
              </Label>
              <Select
                value={config.allowMeetingChat}
                onValueChange={(value) => 
                  updateSessionConfig(sessionType, 'allowMeetingChat', value)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="enabled">Enabled</SelectItem>
                  <SelectItem value="disabled">Disabled</SelectItem>
                  <SelectItem value="limited">Limited</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Recording & Features */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Video className="w-5 h-5" />
              Recording & Features
            </CardTitle>
            <CardDescription>
              Configure recording, transcription, and additional features
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Auto-Record Sessions</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically start recording when meeting begins
                </p>
              </div>
              <Switch
                checked={config.recordAutomatically}
                onCheckedChange={(checked) => 
                  updateSessionConfig(sessionType, 'recordAutomatically', checked)
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Allow Manual Recording</Label>
                <p className="text-sm text-muted-foreground">
                  Participants can start/stop recording manually
                </p>
              </div>
              <Switch
                checked={config.allowRecording}
                onCheckedChange={(checked) => 
                  updateSessionConfig(sessionType, 'allowRecording', checked)
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable Transcription</Label>
                <p className="text-sm text-muted-foreground">
                  Generate live transcripts of the meeting
                </p>
              </div>
              <Switch
                checked={config.allowTranscription}
                onCheckedChange={(checked) => 
                  updateSessionConfig(sessionType, 'allowTranscription', checked)
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Teamwork Reactions</Label>
                <p className="text-sm text-muted-foreground">
                  Allow emoji reactions during the meeting
                </p>
              </div>
              <Switch
                checked={config.allowTeamworkReactions}
                onCheckedChange={(checked) => 
                  updateSessionConfig(sessionType, 'allowTeamworkReactions', checked)
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Entry/Exit Announcements</Label>
                <p className="text-sm text-muted-foreground">
                  Announce when participants join or leave
                </p>
              </div>
              <Switch
                checked={config.isEntryExitAnnounced}
                onCheckedChange={(checked) => 
                  updateSessionConfig(sessionType, 'isEntryExitAnnounced', checked)
                }
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Default Duration (minutes)
              </Label>
              <Input
                type="number"
                min="15"
                max="180"
                value={config.meetingDuration}
                onChange={(e) => 
                  updateSessionConfig(sessionType, 'meetingDuration', parseInt(e.target.value) || 60)
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Watermark Protection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Watermark Protection
            </CardTitle>
            <CardDescription>
              Add watermarks to protect content during screen sharing
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Content Sharing Watermark</Label>
                <p className="text-sm text-muted-foreground">
                  Add watermark when sharing screen content
                </p>
              </div>
              <Switch
                checked={config.watermarkProtection.enabledForContentSharing}
                onCheckedChange={(checked) => 
                  updateSessionConfig(sessionType, 'watermarkProtection.enabledForContentSharing', checked)
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Video Watermark</Label>
                <p className="text-sm text-muted-foreground">
                  Add watermark to video feeds
                </p>
              </div>
              <Switch
                checked={config.watermarkProtection.enabledForVideo}
                onCheckedChange={(checked) => 
                  updateSessionConfig(sessionType, 'watermarkProtection.enabledForVideo', checked)
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => resetToDefaults(sessionType)}
            className="flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Reset to Defaults
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Settings className="w-8 h-8 animate-spin mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Loading configurations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Alert */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          These settings control how Microsoft Teams meetings are configured for different session types. 
          Changes will apply to all new meetings created after saving.
        </AlertDescription>
      </Alert>

      {/* Configuration Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          {sessionConfigs.map((config) => (
            <TabsTrigger
              key={config.sessionType}
              value={config.sessionType}
              className="flex items-center gap-2"
            >
              {config.icon}
              <span className="hidden sm:inline">{config.displayName}</span>
              <span className="sm:hidden">{config.sessionType}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {sessionConfigs.map((config) => (
          <TabsContent key={config.sessionType} value={config.sessionType}>
            {renderConfigurationPanel(config.sessionType)}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default SessionConfiguration;